'use client'

import { useState, useRef, useCallback } from 'react'

interface SwipeOptions {
  threshold?: number
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeUp?: () => void
  onSwipeDown?: () => void
  preventDefaultTouchmoveEvent?: boolean
}

interface SwipeState {
  isDragging: boolean
  startX: number
  startY: number
  currentX: number
  currentY: number
  translateX: number
  translateY: number
  velocity: number
}

export function useSwipeGestures(options: SwipeOptions = {}) {
  const {
    threshold = 50,
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    preventDefaultTouchmoveEvent = true,
  } = options

  const [swipeState, setSwipeState] = useState<SwipeState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    translateX: 0,
    translateY: 0,
    velocity: 0,
  })

  const lastMoveTime = useRef<number>(0)
  const lastMoveX = useRef<number>(0)

  const handleStart = useCallback((clientX: number, clientY: number) => {
    setSwipeState({
      isDragging: true,
      startX: clientX,
      startY: clientY,
      currentX: clientX,
      currentY: clientY,
      translateX: 0,
      translateY: 0,
      velocity: 0,
    })
    lastMoveTime.current = Date.now()
    lastMoveX.current = clientX
  }, [])

  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!swipeState.isDragging) return

      const currentTime = Date.now()
      const timeDiff = currentTime - lastMoveTime.current
      const distanceDiff = clientX - lastMoveX.current

      // Calcular velocidad
      const velocity = timeDiff > 0 ? distanceDiff / timeDiff : 0

      setSwipeState(prev => ({
        ...prev,
        currentX: clientX,
        currentY: clientY,
        translateX: clientX - prev.startX,
        translateY: clientY - prev.startY,
        velocity,
      }))

      lastMoveTime.current = currentTime
      lastMoveX.current = clientX
    },
    [swipeState.isDragging]
  )

  const handleEnd = useCallback(() => {
    if (!swipeState.isDragging) return

    const { translateX, translateY, velocity } = swipeState
    const absX = Math.abs(translateX)
    const absY = Math.abs(translateY)

    // Determinar si es un swipe horizontal o vertical
    const isHorizontalSwipe = absX > absY
    const isVerticalSwipe = absY > absX

    // Verificar si supera el umbral
    const exceedsThreshold = isHorizontalSwipe
      ? absX > threshold
      : absY > threshold
    const hasVelocity = Math.abs(velocity) > 0.1 // Velocidad mínima

    if (exceedsThreshold || hasVelocity) {
      if (isHorizontalSwipe) {
        if (translateX > 0) {
          onSwipeRight?.()
        } else {
          onSwipeLeft?.()
        }
      } else if (isVerticalSwipe) {
        if (translateY > 0) {
          onSwipeDown?.()
        } else {
          onSwipeUp?.()
        }
      }
    }

    // Reset state
    setSwipeState({
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      translateX: 0,
      translateY: 0,
      velocity: 0,
    })
  }, [swipeState, threshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown])

  // Touch events
  const touchHandlers = {
    onTouchStart: (e: React.TouchEvent) => {
      const touch = e.touches[0]
      handleStart(touch.clientX, touch.clientY)
    },
    onTouchMove: (e: React.TouchEvent) => {
      if (preventDefaultTouchmoveEvent) {
        e.preventDefault()
      }
      const touch = e.touches[0]
      handleMove(touch.clientX, touch.clientY)
    },
    onTouchEnd: () => {
      handleEnd()
    },
  }

  // Mouse events (para desktop)
  const mouseHandlers = {
    onMouseDown: (e: React.MouseEvent) => {
      e.preventDefault()
      handleStart(e.clientX, e.clientY)
    },
    onMouseMove: (e: React.MouseEvent) => {
      if (swipeState.isDragging) {
        e.preventDefault()
        handleMove(e.clientX, e.clientY)
      }
    },
    onMouseUp: () => {
      handleEnd()
    },
    onMouseLeave: () => {
      handleEnd()
    },
  }

  return {
    swipeState,
    touchHandlers,
    mouseHandlers,
    isDragging: swipeState.isDragging,
    translateX: swipeState.translateX,
    translateY: swipeState.translateY,
  }
}
